import type { UnzipFileInfo, Unzipped } from 'fflate'
import { ALLOWED_FILE_TYPES } from '@/lib/schemas'

/**
 * Turns what a user dropped or picked into the files we can actually upload.
 *
 * Tender packs arrive as a ZIP or a folder. Folders are already flattened by
 * the browser (react-dropzone's file-selector for a drop, `webkitdirectory`
 * for a pick); ZIPs are opened here, in the browser, so an archive never
 * reaches the server. Everything this returns still goes through the upload
 * surface's own validation and duplicate rules — this only unpacks and tidies.
 */

// ===================
// Limits
// ===================

/** More entries than this and the archive is skipped whole. */
export const MAX_ARCHIVE_ENTRIES = 300

/**
 * Total unpacked size one archive may hold. Five times the largest single file
 * we accept (`validateFile`: 100 MB for a PDF) — room for a real tender pack,
 * while stopping a stray backup archive from filling the tab's memory.
 */
export const MAX_ARCHIVE_UNCOMPRESSED_BYTES = 500 * 1024 * 1024

export interface ExpandLimits {
  maxEntries: number
  maxUncompressedBytes: number
}

const DEFAULT_LIMITS: ExpandLimits = {
  maxEntries: MAX_ARCHIVE_ENTRIES,
  maxUncompressedBytes: MAX_ARCHIVE_UNCOMPRESSED_BYTES,
}

// ===================
// Types
// ===================

export type ArchiveSkipReason =
  | 'nested-archive'
  | 'archive-too-many-files'
  | 'archive-too-large'
  | 'archive-unreadable'

export interface ArchiveSkippedFile {
  name: string
  reason: ArchiveSkipReason
}

export interface ExpandedArchive {
  name: string
  /** Files taken out of the archive (system files excluded). */
  count: number
}

export interface ExpandUploadFilesResult {
  files: File[]
  skipped: ArchiveSkippedFile[]
  expandedArchives: ExpandedArchive[]
}

// ===================
// ZIP detection
// ===================

const ZIP_MIME_TYPES = ['application/zip', 'application/x-zip-compressed']

/**
 * Extra `accept` entries for upload surfaces that take a ZIP. Browser-side only:
 * a ZIP is opened before upload and must never be added to the server-bound
 * allow-list (`ALLOWED_FILE_TYPES`).
 */
export const ARCHIVE_ACCEPT_TYPES: Record<string, string[]> = Object.fromEntries(
  ZIP_MIME_TYPES.map((mime) => [mime, ['.zip']])
)

export function isZipFile(file: Pick<File, 'name' | 'type'>): boolean {
  return (
    file.name.toLowerCase().endsWith('.zip') ||
    ZIP_MIME_TYPES.includes(file.type.toLowerCase())
  )
}

// ===================
// Paths and system files
// ===================

const SYSTEM_FILE_NAMES = new Set(['thumbs.db', 'desktop.ini'])

function pathSegments(path: string): string[] {
  return path.split(/[\\/]/).filter((segment) => segment.length > 0 && segment !== '.')
}

export function basename(path: string): string {
  const segments = pathSegments(path)
  return segments[segments.length - 1] ?? path
}

/**
 * OS clutter that nobody meant to upload: macOS resource forks (`__MACOSX/`),
 * `.DS_Store` and other dotfiles, anything inside a hidden folder, and the
 * Windows thumbnail cache. Skipped silently — naming them would be noise.
 */
export function isSystemPath(path: string): boolean {
  const segments = pathSegments(path)
  if (segments.length === 0) return true
  if (segments.some((segment) => segment === '__MACOSX' || segment.startsWith('.'))) return true
  return SYSTEM_FILE_NAMES.has(segments[segments.length - 1]!.toLowerCase())
}

/**
 * The path the file had inside what was dropped or picked. A folder pick sets
 * `webkitRelativePath`; react-dropzone's file-selector sets `path` on dropped
 * folder contents. Falls back to the bare name.
 */
function sourcePath(file: File): string {
  if (file.webkitRelativePath) return file.webkitRelativePath
  const withPath = file as File & { path?: unknown }
  return typeof withPath.path === 'string' && withPath.path ? withPath.path : file.name
}

// ===================
// MIME inference
// ===================

const MIME_BY_EXTENSION: Record<string, string> = Object.fromEntries(
  Object.entries(ALLOWED_FILE_TYPES).flatMap(([mime, config]) =>
    config.extensions.map((ext) => [ext.toLowerCase(), mime])
  )
)

/**
 * The MIME type a browser would give a file with this name, for the types we
 * upload. Unknown extensions get `''` — what a browser reports for a type it
 * doesn't recognise — so validation names them as unsupported.
 */
export function inferMimeType(filename: string): string {
  const dot = filename.lastIndexOf('.')
  if (dot <= 0) return ''
  return MIME_BY_EXTENSION[filename.slice(dot).toLowerCase()] ?? ''
}

// ===================
// Reading
// ===================

function readAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'))
    reader.readAsArrayBuffer(file)
  })
}

// ===================
// Archive internals
// ===================

const EOCD_SIGNATURE = 0x06054b50
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50
const ZIP64_EOCD_SIGNATURE = 0x06064b50
const CENTRAL_HEADER_SIGNATURE = 0x02014b50
const MAX_EOCD_SEARCH = 65557 // 22-byte record + the longest possible comment

/**
 * True when any entry is password-protected (general-purpose flag bit 0 in
 * the central directory). fflate doesn't check this and would hand back
 * scrambled bytes for an encrypted, uncompressed entry, so look first.
 * Anything unexpected in the directory counts as unreadable.
 */
export function hasEncryptedEntries(bytes: Uint8Array): boolean {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const u16 = (at: number) => view.getUint16(at, true)
  const u32 = (at: number) => view.getUint32(at, true)

  let eocd = bytes.length - 22
  const floor = Math.max(0, bytes.length - MAX_EOCD_SEARCH)
  while (eocd >= floor && u32(eocd) !== EOCD_SIGNATURE) eocd--
  if (eocd < floor) throw new Error('No end of central directory')

  let count = u16(eocd + 8)
  let offset = u32(eocd + 16)
  if (eocd >= 20 && u32(eocd - 20) === ZIP64_LOCATOR_SIGNATURE) {
    const zip64Eocd = Number(view.getBigUint64(eocd - 12, true))
    if (zip64Eocd + 56 <= bytes.length && u32(zip64Eocd) === ZIP64_EOCD_SIGNATURE) {
      count = Number(view.getBigUint64(zip64Eocd + 32, true))
      offset = Number(view.getBigUint64(zip64Eocd + 48, true))
    }
  }

  for (let i = 0; i < count; i++) {
    if (offset + 46 > bytes.length || u32(offset) !== CENTRAL_HEADER_SIGNATURE) {
      throw new Error('Malformed central directory')
    }
    if (u16(offset + 8) & 1) return true
    offset += 46 + u16(offset + 28) + u16(offset + 30) + u16(offset + 32)
  }
  return false
}

function unzipAsync(
  bytes: Uint8Array,
  filter: (file: UnzipFileInfo) => boolean
): Promise<Unzipped> {
  return import('fflate').then(
    ({ unzip }) =>
      new Promise<Unzipped>((resolve, reject) => {
        unzip(bytes, { filter }, (error, files) => (error ? reject(error) : resolve(files)))
      })
  )
}

class ArchiveSkip extends Error {
  constructor(readonly reason: ArchiveSkipReason) {
    super(reason)
  }
}

interface ArchiveContents {
  files: File[]
  nestedArchives: string[]
}

async function openArchive(archive: File, limits: ExpandLimits): Promise<ArchiveContents> {
  const bytes = new Uint8Array(await readAsArrayBuffer(archive))

  // First pass reads only the directory: names and declared sizes, so the
  // guards run before anything is unpacked. fflate calls back even when every
  // entry is filtered out.
  const listed: UnzipFileInfo[] = []
  try {
    if (hasEncryptedEntries(bytes)) throw new ArchiveSkip('archive-unreadable')
    await unzipAsync(bytes, (file) => {
      listed.push(file)
      return false
    })
  } catch {
    // Corrupt, truncated, not really a ZIP, or password-protected.
    throw new ArchiveSkip('archive-unreadable')
  }

  const wanted = listed.filter((file) => !file.name.endsWith('/') && !isSystemPath(file.name))
  if (wanted.length > limits.maxEntries) throw new ArchiveSkip('archive-too-many-files')

  const nestedArchives = wanted
    .filter((file) => isZipFile({ name: basename(file.name), type: '' }))
    .map((file) => basename(file.name))
  const toExtract = new Set(
    wanted.filter((file) => !isZipFile({ name: basename(file.name), type: '' })).map((f) => f.name)
  )

  const declaredTotal = wanted
    .filter((file) => toExtract.has(file.name))
    .reduce((sum, file) => sum + file.originalSize, 0)
  if (declaredTotal > limits.maxUncompressedBytes) throw new ArchiveSkip('archive-too-large')

  // Second pass unpacks. fflate inflates large entries off the main thread,
  // so the page stays responsive on a big pack.
  let unpacked: Unzipped
  try {
    unpacked = await unzipAsync(bytes, (file) => toExtract.has(file.name))
  } catch {
    throw new ArchiveSkip('archive-unreadable')
  }

  // Directory order, not `unpacked`'s key order (an all-digit name would sort first).
  let unpackedTotal = 0
  const files: File[] = []
  for (const entry of wanted) {
    const data = unpacked[entry.name]
    if (!data || !toExtract.has(entry.name)) continue
    unpackedTotal += data.byteLength
    if (unpackedTotal > limits.maxUncompressedBytes) throw new ArchiveSkip('archive-too-large')
    const name = basename(entry.name)
    files.push(new File([data], name, { type: inferMimeType(name) }))
  }

  return { files, nestedArchives }
}

// ===================
// Public API
// ===================

/**
 * Expand ZIPs, drop system files, and report what was left out. Input order is
 * kept; an archive's files take the archive's place, in archive order.
 */
export async function expandUploadFiles(
  input: File[],
  limits: ExpandLimits = DEFAULT_LIMITS
): Promise<ExpandUploadFilesResult> {
  const files: File[] = []
  const skipped: ArchiveSkippedFile[] = []
  const expandedArchives: ExpandedArchive[] = []

  for (const file of input) {
    if (isSystemPath(sourcePath(file))) continue

    if (!isZipFile(file)) {
      files.push(file)
      continue
    }

    try {
      const contents = await openArchive(file, limits)
      files.push(...contents.files)
      skipped.push(
        ...contents.nestedArchives.map((name) => ({ name, reason: 'nested-archive' as const }))
      )
      expandedArchives.push({ name: file.name, count: contents.files.length })
    } catch (error) {
      skipped.push({
        name: file.name,
        reason: error instanceof ArchiveSkip ? error.reason : 'archive-unreadable',
      })
    }
  }

  return { files, skipped, expandedArchives }
}
